import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card';
import { Input, Label, Textarea } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { RichTextEditor } from '@/features/editor/RichTextEditor';
import { ImageDropzone } from '@/features/media/ImageDropzone';
import { usePageEditor } from '@/features/page-builder/usePageEditor';
import { AIFieldAdornment } from '@/features/ai/AIFieldAdornment';
import type { BlockInstance } from '@sytel/blocks';

const ID = () => (typeof crypto !== 'undefined' && 'randomUUID' in crypto
  ? crypto.randomUUID()
  : `block-${Math.random().toString(36).slice(2, 10)}-${Date.now()}`);

interface PostClassicEditorProps {
  /** Featured image URL (stored in editor.advanced.preview_image). */
  featuredImage: string | null;
  onFeaturedImageChange: (url: string | null) => void;
  /** Excerpt / summary. */
  excerpt: string;
  onExcerptChange: (v: string) => void;
  /** Optional field schema from PostType (e.g. category). */
  customFields?: Array<{ name: string; label: string; type: string }>;
  /** Custom fields current values (post.data). */
  customValues?: Record<string, unknown>;
  onCustomValuesChange?: (next: Record<string, unknown>) => void;
  /** Panel de taxonomías (categorías/etiquetas) inyectado en la sidebar. */
  termsSlot?: ReactNode;
}

/**
 * Editor estilo "WordPress Classic":
 * - Título grande arriba
 * - Editor enriquecido (Tiptap) ocupando la mayor parte
 * - Sidebar con metadatos: extracto, imagen destacada, categoría, slug
 *
 * Internamente, el contenido se guarda como UN ÚNICO bloque `rich-text` para
 * mantener compatibilidad con el resto del stack (frontend, exports, plantillas).
 * Si el usuario quiere bloques avanzados, puede cambiar al "Page builder" desde
 * la cabecera del editor de posts.
 */
export function PostClassicEditor({
  featuredImage,
  onFeaturedImageChange,
  excerpt,
  onExcerptChange,
  customFields = [],
  customValues = {},
  onCustomValuesChange,
  termsSlot,
}: PostClassicEditorProps) {
  const editor = usePageEditor();

  // Find the existing rich-text block, or create a virtual default state.
  const richBlock = useMemo<BlockInstance | null>(() => {
    return editor.blocks.find((b) => b.type === 'rich-text') ?? null;
  }, [editor.blocks]);

  function readHtml(block: BlockInstance | null): string {
    if (!block) return '';
    const data = block.data as { html?: unknown };
    return typeof data?.html === 'string' ? data.html : '';
  }

  // HTML state mirrors the rich-text block content.
  const [html, setHtml] = useState<string>(() => readHtml(richBlock));

  // Keep local HTML in sync when block changes externally (e.g. init).
  useEffect(() => {
    setHtml(readHtml(richBlock));
  }, [richBlock?.id]);

  function handleHtmlChange(next: string) {
    setHtml(next);
    if (richBlock) {
      const data = (richBlock.data ?? {}) as Record<string, unknown>;
      editor.updateBlockData(richBlock.id, { ...data, html: next });
    } else {
      // First content edit — create the rich-text block at index 0
      const newBlock: BlockInstance = {
        id: ID(),
        type: 'rich-text',
        order: 0,
        locale: editor.locale,
        visible: true,
        data: { html: next, width: 'narrow' },
      };
      editor.insertBlock(newBlock, 0);
    }
  }

  return (
    <div className="grid grid-cols-1 gap-6 overflow-y-auto bg-neutral-50 p-6 lg:grid-cols-[1fr_320px]">
      {/* MAIN COLUMN */}
      <div className="space-y-4">
        {/* Hero title */}
        <div className="rounded-lg border border-neutral-200 bg-white p-6">
          <div className="flex items-start gap-2">
            <input
              type="text"
              value={editor.title}
              onChange={(e) => editor.setMeta({ title: e.target.value })}
              placeholder="Título del artículo…"
              className="flex-1 border-0 bg-transparent text-3xl font-bold tracking-tight outline-none placeholder:text-neutral-300"
            />
            <AIFieldAdornment
              value={editor.title || html.replace(/<[^>]+>/g, ' ').slice(0, 600)}
              onChange={(t) => {
                const first = t.split('\n').map((s) => s.replace(/^Título sugerido \d+:\s*/i, '').trim()).find(Boolean);
                editor.setMeta({ title: first ?? t });
              }}
              preset="title"
              context={{ metadata: { contentSnippet: html.replace(/<[^>]+>/g, ' ').slice(0, 600) } }}
              variant="ghost"
              size="sm"
              label="AI"
            />
          </div>
          <div className="mt-1 flex items-center gap-2 text-xs text-neutral-500">
            <span>URL:</span>
            <span>/{editor.slug || 'sin-slug'}</span>
            <button
              type="button"
              onClick={() => {
                const next = window.prompt('Slug de la entrada (sin /):', editor.slug);
                if (next !== null) editor.setMeta({ slug: slugify(next) });
              }}
              className="text-neutral-900 underline hover:no-underline"
            >
              Editar
            </button>
          </div>
        </div>

        {/* Body editor */}
        <div className="overflow-hidden rounded-lg border border-neutral-200 bg-white">
          <RichTextEditor
            value={html}
            onChange={handleHtmlChange}
            placeholder="Empieza a escribir tu artículo… Usa la barra de herramientas para dar formato."
            minHeight="480px"
          />
        </div>

        <p className="text-xs text-neutral-400">
          {countWords(html)} palabras · {readingTime(html)} min de lectura
        </p>
      </div>

      {/* SIDEBAR */}
      <aside className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Imagen destacada</CardTitle>
            <CardDescription>Se mostrará en listados y como Open Graph por defecto.</CardDescription>
          </CardHeader>
          <CardContent>
            <ImageDropzone
              value={featuredImage}
              onChange={onFeaturedImageChange}
              aspectClass="aspect-video"
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-start justify-between gap-2">
              <div>
                <CardTitle className="text-sm">Extracto</CardTitle>
                <CardDescription>Resumen breve para listados y SEO.</CardDescription>
              </div>
              <AIFieldAdornment
                value={excerpt || html.replace(/<[^>]+>/g, ' ').slice(0, 1500)}
                onChange={(t) => onExcerptChange(t.slice(0, 500))}
                preset="excerpt"
                context={{ metadata: { title: editor.title } }}
                variant="ghost"
                size="sm"
                label="AI"
              />
            </div>
          </CardHeader>
          <CardContent>
            <Textarea
              value={excerpt}
              onChange={(e) => onExcerptChange(e.target.value)}
              rows={4}
              maxLength={500}
              placeholder="Resumen del artículo (máx 500 caracteres)…"
            />
            <p className="mt-1 text-right text-[10px] text-neutral-400">
              {excerpt.length} / 500
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Publicación</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-1">
              <Label className="text-xs">Estado</Label>
              <select
                value={editor.status}
                onChange={(e) => editor.setMeta({ status: e.target.value as typeof editor.status })}
                className="h-9 w-full rounded-md border border-neutral-200 bg-white px-2 text-sm"
              >
                <option value="draft">Borrador</option>
                <option value="published">Publicado</option>
                <option value="scheduled">Programado</option>
                <option value="archived">Archivado</option>
              </select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Idioma</Label>
              <select
                value={editor.locale}
                onChange={(e) => editor.setMeta({ locale: e.target.value })}
                className="h-9 w-full rounded-md border border-neutral-200 bg-white px-2 text-sm"
              >
                <option value="es">Español</option>
                <option value="en">English</option>
              </select>
            </div>
            <div className="flex flex-wrap gap-1.5">
              <Badge variant={editor.status === 'published' ? 'success' : 'secondary'}>
                {editor.status}
              </Badge>
              {richBlock ? null : <Badge variant="outline">Sin contenido</Badge>}
            </div>
          </CardContent>
        </Card>

        {termsSlot}

        {customFields.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Campos personalizados</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {customFields.map((f) => (
                <CustomFieldRenderer
                  key={f.name}
                  field={f}
                  value={customValues[f.name]}
                  onChange={(v) =>
                    onCustomValuesChange?.({ ...customValues, [f.name]: v })
                  }
                />
              ))}
            </CardContent>
          </Card>
        )}
      </aside>
    </div>
  );
}

function CustomFieldRenderer({
  field, value, onChange,
}: {
  field: { name: string; label: string; type: string };
  value: unknown;
  onChange: (v: unknown) => void;
}) {
  const v = (value as string | number | undefined) ?? '';

  if (field.type === 'number') {
    return (
      <div className="space-y-1">
        <Label className="text-xs">{field.label}</Label>
        <Input
          type="number"
          value={v as number}
          onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
        />
      </div>
    );
  }

  if (field.type === 'image') {
    return <CustomImageField field={field} value={v as string} onChange={onChange} />;
  }

  return (
    <div className="space-y-1">
      <Label className="text-xs">{field.label}</Label>
      <Input
        value={v as string}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

function CustomImageField({
  field, value, onChange,
}: { field: { label: string }; value: string; onChange: (v: string) => void }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs">{field.label}</Label>
      <ImageDropzone
        value={value || null}
        onChange={(url) => onChange(url ?? '')}
        aspectClass="aspect-video"
        label="Elegir imagen"
      />
    </div>
  );
}

function slugify(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 100);
}

function countWords(html: string): number {
  const text = html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  if (!text) return 0;
  return text.split(' ').length;
}

function readingTime(html: string): number {
  return Math.max(1, Math.round(countWords(html) / 220));
}
